import { useMemo, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import {
  iconLeaderboardAvgPoints,
  iconLeaderboardFirstPlace,
} from '../../assets';
import { useTheme } from '../../theme';
import type { LeaderboardRow } from './types';
import { getInitials } from './utils';

export interface LeaderboardTableProps {
  rows: LeaderboardRow[];
}

export function LeaderboardTable({ rows }: LeaderboardTableProps) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const lb = theme.palette.adminLeaderboard;
  const layout = theme.layout.adminLeaderboard;

  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language === 'es' ? 'es' : 'en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
    [i18n.language]
  );

  const numFmt = useMemo(
    () =>
      new Intl.NumberFormat(i18n.language === 'es' ? 'es' : 'en-US', {
        maximumFractionDigits: 0,
      }),
    [i18n.language]
  );

  const cellBase: CSSProperties = {
    padding: `${lb.tableCellPaddingY} ${lb.tableCellPaddingX}`,
    textAlign: 'left',
    verticalAlign: 'middle',
    fontSize: theme.typography.fontSize.base,
    borderBottom: `${theme.shape.borderWidth.thin} solid ${lb.tableRowBorderBottom}`,
  };

  const thStyle: CSSProperties = {
    ...cellBase,
    fontWeight: theme.typography.fontWeight.bold,
    color: lb.tableHeaderText,
    fontSize: theme.typography.fontSize.base,
    borderBottom: `${theme.shape.borderWidth.thin} solid ${lb.tableHeaderBorderBottom}`,
    backgroundColor: lb.cardBackground,
  };

  return (
    <div
      style={{
        width: '100%',
        overflowX: 'auto',
        backgroundColor: lb.cardBackground,
      }}
    >
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          minWidth: 640,
        }}
      >
        <thead>
          <tr>
            <th
              style={{
                ...thStyle,
                minWidth: layout.colRankMinWidth,
                textAlign: 'center',
              }}
            >
              {t('leaderboard.table.rank')}
            </th>
            <th style={{ ...thStyle, minWidth: layout.colSalesRepMinWidth }}>
              {t('leaderboard.table.salesRep')}
            </th>
            <th style={{ ...thStyle, minWidth: layout.colPointsMinWidth }}>
              {t('leaderboard.table.totalPoints')}
            </th>
            <th
              style={{
                ...thStyle,
                minWidth: layout.colCoursesMinWidth,
                textAlign: 'center',
              }}
            >
              {t('leaderboard.table.coursesCompleted')}
            </th>
            <th
              style={{
                ...thStyle,
                minWidth: layout.colLastActiveMinWidth,
                textAlign: 'center',
              }}
            >
              {t('leaderboard.table.lastActive')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            const d =
              row.lastActive instanceof Date
                ? row.lastActive
                : new Date(row.lastActive);
            const lastActiveLabel = Number.isNaN(d.getTime())
              ? '—'
              : dateFmt.format(d);
            const bg =
              row.rank === 1
                ? lb.rowBackground
                : row.rank === 2
                  ? lb.rowBackground
                  : row.rank === 3
                    ? lb.rowBackground
                    : lb.cardBackground;
            const isTopThree = row.rank <= 3;

            return (
              <tr
                key={`${row.rank}-${row.userId}`}
                style={{
                  backgroundColor: bg,
                  minHeight: lb.tableRowHeightMin,
                }}
              >
                <td
                  style={{
                    ...cellBase,
                    minWidth: layout.colRankMinWidth,
                    textAlign: 'center',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: lb.rankMedalCircleSize,
                    }}
                  >
                    {isTopThree ? (
                      <div
                        style={{
                          width: lb.rankMedalCircleSize,
                          height: lb.rankMedalCircleSize,
                          borderRadius: theme.shape.borderRadius.full,
                          background:
                            row.rank === 1
                              ? lb.rankTop1MedalGradient
                              : row.rank === 2
                                ? lb.rankTop2MedalGradient
                                : lb.rankTop3MedalGradient,
                          display: 'grid',
                          placeItems: 'center',
                          flexShrink: 0,
                          boxSizing: 'border-box',
                        }}
                        aria-hidden
                      >
                        <img
                          src={iconLeaderboardFirstPlace}
                          alt=""
                          width={lb.rankMedalIconSize}
                          height={lb.rankMedalIconSize}
                          style={{
                            display: 'block',
                            objectFit: 'contain',
                          }}
                        />
                      </div>
                    ) : (
                      <span
                        style={{
                          fontWeight: theme.typography.fontWeight.semibold,
                          color: lb.rankTextMuted,
                        }}
                      >
                        #{row.rank}
                      </span>
                    )}
                  </div>
                </td>
                <td
                  style={{ ...cellBase, minWidth: layout.colSalesRepMinWidth }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: theme.spacing.md,
                      minWidth: 0,
                    }}
                  >
                    <div
                      style={{
                        width: lb.avatarSize,
                        height: lb.avatarSize,
                        borderRadius: theme.shape.borderRadius.full,
                        backgroundColor: lb.avatarBackground,
                        color: lb.avatarText,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: theme.typography.fontSize.sm,
                        fontWeight: theme.typography.fontWeight.semibold,
                        flexShrink: 0,
                      }}
                      aria-hidden
                    >
                      {getInitials(row.name)}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: theme.typography.fontWeight.bold,
                          color: lb.salesRepNameText,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {row.name}
                      </div>
                      <div
                        style={{
                          fontSize: theme.typography.fontSize.sm,
                          color: lb.salesRepEmailText,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {row.email}
                      </div>
                    </div>
                  </div>
                </td>
                <td style={{ ...cellBase, minWidth: layout.colPointsMinWidth }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: theme.spacing.xs2,
                    }}
                  >
                    <span
                      aria-hidden
                      style={{
                        display: 'inline-block',
                        width: lb.pointsIconSize,
                        height: lb.pointsIconSize,
                        flexShrink: 0,
                        backgroundColor: lb.statValuePoints,
                        maskImage: `url(${iconLeaderboardAvgPoints})`,
                        WebkitMaskImage: `url(${iconLeaderboardAvgPoints})`,
                        maskSize: 'contain',
                        WebkitMaskSize: 'contain',
                        maskRepeat: 'no-repeat',
                        WebkitMaskRepeat: 'no-repeat',
                        maskPosition: 'center',
                        WebkitMaskPosition: 'center',
                      }}
                    />
                    <span
                      style={{
                        fontWeight: theme.typography.fontWeight.bold,
                        color: lb.statValuePoints,
                        fontSize: theme.typography.fontSize.lg,
                      }}
                    >
                      {numFmt.format(row.totalPoints)}
                    </span>
                  </div>
                </td>
                <td
                  style={{
                    ...cellBase,
                    minWidth: layout.colCoursesMinWidth,
                    textAlign: 'center',
                    fontWeight: isTopThree
                      ? theme.typography.fontWeight.semibold
                      : theme.typography.fontWeight.normal,
                    color: lb.titleText,
                  }}
                >
                  {numFmt.format(row.coursesCompleted)}
                </td>
                <td
                  style={{
                    ...cellBase,
                    minWidth: layout.colLastActiveMinWidth,
                    textAlign: 'center',
                    color: lb.lastActiveText,
                    fontSize: theme.typography.fontSize.sm,
                  }}
                >
                  {lastActiveLabel}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
